import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';
import {useAppDispatch} from '../../hooks/redux';
import { store } from '../../store';

import { setFavourites } from '../../slices/siteFavouritesSlice';
import { setLoading } from '../../slices/loadingSlice';
import { FavouritesApiService } from '../../api';
import {itemTypes} from '../../enums/ItemTypes.enum';
import i18n from '../../i18n';
import { RootState } from '../../types';
import DataTable from '../DataTable/DataTable';
import ViewHeader from '../ViewHeader';
import BookmarkViewActions from './BookmarkViewActions';

const BookmarkOverview = () => {
    const currentUser = useSelector((state: RootState) => state.user)
    const bookmarkFilter = useSelector((state: RootState) => state.bookmarkFilter)
    const bookmarkCards = useSelector((state: RootState) => state.bookmarkCards)
    const siteFavourites = useSelector((state: RootState) => state.siteFavourites)
    const showSiteFavOnly = useSelector((state: RootState) => state.showSiteFavOnly)

    const dispatch = useAppDispatch()
    
    // Log state changes when siteFavourites changes
    useEffect(() => {
        console.log('Current siteFavourites state:', siteFavourites);
    }, [siteFavourites]);
    
    // Log current state on component mount
    useEffect(() => {
        console.log('Component mounted - Current state:', {
            user: currentUser,
            bookmarkCards,
            siteFavourites,
            bookmarkFilter,
            showSiteFavOnly
        });
    }, []);

    useEffect(() => {
        const favApi = new FavouritesApiService()
        async function getSiteFavs() {
            // Simple loading tracking with a unique ID
            const loadingOperationId = `bookmarks-fetch-${Date.now()}`;
            console.log(`BookmarkView [${loadingOperationId}]: Starting site favorites fetch`);
            
            // Set loading state
            dispatch(setLoading(true));
            
            try {
                // Fetch favorites from the API
                const siteResponse: any = await favApi.getCurrentEntityFav(currentUser.whid)
                
                // Handle different response formats (direct or nested in favorite_bookmarks)
                let mySiteFav: any[] = [];
                if (siteResponse && siteResponse['favorite_bookmarks']) {
                    mySiteFav = siteResponse['favorite_bookmarks'];
                } else if (Array.isArray(siteResponse)) {
                    mySiteFav = siteResponse;
                } else {
                    console.warn('Unexpected response format:', siteResponse);
                }
                
                console.log(`BookmarkView [${loadingOperationId}]: Found ${mySiteFav?.length || 0} bookmarks`);
                
                // Update the favorites in the Redux store
                dispatch(setFavourites(mySiteFav))
            } catch (error) {
                console.error('Error fetching site favorites:', error);
                // Could dispatch an error action here if needed
            } finally {
                // Always set loading to false when done
                dispatch(setLoading(false));
                console.log(`BookmarkView [${loadingOperationId}]: Site favorites fetch complete`);
            }
        }
        // Wait till whid is available to avoid invalid API requests
        currentUser.whid && getSiteFavs();
    }, [currentUser.whid, dispatch]);

    const originalCardData = showSiteFavOnly ? siteFavourites : bookmarkCards
    const itemType = showSiteFavOnly ? itemTypes.siteFavourites : itemTypes.bookmarkCard

    // Use explicit typing for filteredCardData
    let filteredCardData: any[] = []
    if (bookmarkFilter) {
        filteredCardData = originalCardData.filter((bmk: any) => bmk.entity === bookmarkFilter)
    } else {
        filteredCardData = originalCardData
    }

    // Site favourites contains redundant bookmarks id
    const displayView = <DataTable filteredItems={filteredCardData} itemType={itemType} actions={BookmarkViewActions()} isGlobalSearching={!showSiteFavOnly} />
    const bookmarkScope = showSiteFavOnly ? `${currentUser.whid || "WHID"}` : i18n.t('universal:global')
    return (
        <div>
            <ViewHeader
                title={`${bookmarkScope} ${i18n.t('universal:bookmarks')}`}
            />
            {displayView}
        </div>
    );
}

export default function() {
    return (
        <BookmarkOverview />
    );
}
